import { createFileRoute, Link } from "@tanstack/react-router";
import { useSEO } from "../hooks/useSEO";
import { SEO_CONFIG } from "../seo/seoConfig";
import { JsonLd } from "../seo/JsonLd";
import { howToSchema } from "../seo/schemas";
import { Breadcrumb } from "../components/shared/Breadcrumb";

export const Route = createFileRoute("/mcp")({
  component: McpDocsPage,
});

/** The 9 live MCP tools (apps/mcp), described in plain English per the server's own instruction. */
const MCP_TOOLS: { name: string; what: string }[] = [
  { name: "List clients", what: "See every client in the agency account and their connected platforms." },
  { name: "Client summary", what: "Pull a client's headline KPIs for a date range." },
  { name: "Compare two periods", what: "Put this month against last (or any two ranges) for a client." },
  { name: "Metric breakdown", what: "Break a metric down by a dimension — by campaign, by ad set, by channel." },
  { name: "Top performers", what: "Rank the best-performing campaigns, ads, or clients on a metric." },
  { name: "Search metrics", what: "Find which metrics are available for a client across their platforms." },
  { name: "List reports", what: "See the reports that already exist for a client." },
  { name: "Task status", what: "Check on a long-running job." },
  { name: "Create report", what: "Draft a new report (created as a draft — never auto-published to a client)." },
];

const HOW_IT_WORKS: { name: string; text: string }[] = [
  { name: "Connect ProvenLeap to Claude", text: "Add the ProvenLeap MCP server to Claude. It authenticates against your agency account and only ever sees your own tenant's data." },
  { name: "Ask in plain language", text: "Ask Claude about a client — \"how did FreshBrew's Google Ads do this month vs last?\" Claude picks the right tool and calls ProvenLeap." },
  { name: "Get an answer you can act on", text: "Claude returns the numbers with context, and can draft a report — so you go from question to deliverable without leaving the conversation." },
];

function McpDocsPage() {
  useSEO(SEO_CONFIG["/mcp"]!);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd
        data={howToSchema({
          name: "How to use Claude AI with ProvenLeap via MCP",
          description:
            "Connect ProvenLeap's MCP server to Claude and work with your agency's marketing data in natural language.",
          steps: HOW_IT_WORKS,
        })}
      />
      <div className="mx-auto max-w-3xl px-4 py-12 sm:py-16">
        <div className="mb-6">
          <Breadcrumb items={[{ name: "Home", url: "/" }, { name: "MCP Integration", url: "/mcp" }]} />
        </div>

        <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
          ProvenLeap's Claude AI Integration (MCP)
        </h1>

        <p className="mt-5 text-[18px] leading-relaxed text-foreground/90">
          <strong>ProvenLeap connects to AI assistants like Claude and ChatGPT through the
          Model Context Protocol (MCP).</strong> Connect ProvenLeap to Claude or ChatGPT and ask about a client's
          performance in plain language, the assistant reads your live marketing data and can even draft a report,
          without you exporting anything to a separate AI tool.
        </p>

        <section className="mt-10" aria-labelledby="what-heading">
          <h2 id="what-heading" className="text-2xl font-semibold tracking-tight">What is MCP, and why does it matter for agency reporting?</h2>
          <p className="mt-3 text-[17px] leading-relaxed text-foreground/90">
            The <strong>Model Context Protocol (MCP)</strong> is an open standard from Anthropic that lets an AI
            assistant like Claude securely connect to external tools and data. ProvenLeap runs an MCP server, so
            Claude can query your agency's marketing analytics directly. For an agency, that turns reporting from
            an export-and-paste chore into a conversation: ask a question, get the numbers, act on them.
          </p>
          <p className="mt-3 text-[17px] leading-relaxed text-foreground/90">
            Learn more about the standard at{" "}
            <a
              href="https://modelcontextprotocol.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand underline underline-offset-2"
            >
              modelcontextprotocol.io
            </a>.
          </p>
        </section>

        <section className="mt-10" aria-labelledby="how-heading">
          <h2 id="how-heading" className="text-2xl font-semibold tracking-tight">How does it work?</h2>
          <p className="mt-3 text-[17px] leading-relaxed text-foreground/90">
            The flow is: <strong>You → Claude → ProvenLeap MCP → your marketing data</strong>. Everything is
            scoped to your own agency account with strict tenant isolation — Claude can only see your data.
          </p>
          <ol className="mt-4 space-y-3">
            {HOW_IT_WORKS.map((step, i) => (
              <li key={step.name} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand/10 text-sm font-semibold text-brand">
                  {i + 1}
                </span>
                <span className="text-[17px] leading-relaxed text-foreground/90">
                  <strong>{step.name}.</strong> {step.text}
                </span>
              </li>
            ))}
          </ol>
        </section>

        <section className="mt-10" aria-labelledby="tools-heading">
          <h2 id="tools-heading" className="text-2xl font-semibold tracking-tight">What can Claude do with ProvenLeap?</h2>
          <p className="mt-3 text-[17px] leading-relaxed text-foreground/90">
            ProvenLeap exposes nine tools to Claude. Eight are read-only analytics; the ninth drafts a report
            (always as a draft — Claude can never publish to a client on its own).
          </p>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full border-collapse text-[15px]">
              <thead className="bg-muted/50">
                <tr>
                  <th className="border-[0.5px] border-input px-3 py-2 text-left font-semibold">Capability</th>
                  <th className="border-[0.5px] border-input px-3 py-2 text-left font-semibold">What it does</th>
                </tr>
              </thead>
              <tbody>
                {MCP_TOOLS.map((tool) => (
                  <tr key={tool.name}>
                    <td className="border-[0.5px] border-input px-3 py-2 font-medium">{tool.name}</td>
                    <td className="border-[0.5px] border-input px-3 py-2">{tool.what}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-10" aria-labelledby="usecases-heading">
          <h2 id="usecases-heading" className="text-2xl font-semibold tracking-tight">Example ways agencies use it</h2>
          <ul className="mt-3 list-disc space-y-1.5 pl-6 text-[17px] text-foreground/90">
            <li>"How did this client's Google Ads perform this month versus last?"</li>
            <li>"Which campaigns had the best ROAS across all my clients last quarter?"</li>
            <li>"Draft a monthly performance report for this client."</li>
            <li>"What metrics do I have available for this client's LinkedIn Ads?"</li>
          </ul>
        </section>

        <section className="mt-10" aria-labelledby="start-heading">
          <h2 id="start-heading" className="text-2xl font-semibold tracking-tight">Getting started</h2>
          <p className="mt-3 text-[17px] leading-relaxed text-foreground/90">
            When you connect ProvenLeap to Claude, you add the ProvenLeap MCP server and authorize it against your
            agency account. From then on, Claude can answer questions about your clients and draft reports. The
            connection is read-oriented and tenant-isolated by design.
          </p>
          <div className="mt-6">
            <Link to="/waitlist" className="text-brand underline underline-offset-2 hover:text-brand/80">
              Join the waitlist to get started →
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
